import React, { useEffect, useState } from "react";
import axios from "axios";
import "./AdsAnalytics.css";

function AdsAnalytics() {
  const [ads, setAds]               = useState([]);
  const [selectedAd, setSelectedAd] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch]         = useState("");
  const [filter, setFilter]         = useState("all");
  const adsPerPage = 8;

  const BASE_URL =
    window.location.hostname === "localhost"
      ? "http://localhost:5000"
      : "https://success-blog-app-backend.onrender.com";

  useEffect(() => { fetchAds(); }, []);

  const fetchAds = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/ads/all-ads`);
      setAds(res.data);
    } catch (err) { console.error(err); }
  };

  // ── FILTER ──
  const filtered = ads.filter(ad => {
    const matchSearch = (ad.text || ad.adType || "").toLowerCase().includes(search.toLowerCase());
    const matchFilter =
      filter === "all"     ? true :
      filter === "running" ? ad.views < ad.maxViews :
      filter === "stopped" ? ad.views >= ad.maxViews : true;
    return matchSearch && matchFilter;
  });

  // ── STATS ──
  const totalViews   = ads.reduce((s, a) => s + (a.views || 0), 0);
  const runningCount = ads.filter(a => a.views < a.maxViews).length;
  const stoppedCount = ads.filter(a => a.views >= a.maxViews).length;

  // ── PAGINATION ──
  const totalPages  = Math.ceil(filtered.length / adsPerPage);
  const currentAds  = filtered.slice((currentPage - 1) * adsPerPage, currentPage * adsPerPage);

  const getProgress = (ad) => Math.min(100, Math.round((ad.views / (ad.maxViews || 1)) * 100));

  const TYPE_ICONS = { text: "📝", image: "🖼", video: "🎥" };

  return (
    <div className="aa-wrapper">

      {/* HEADER */}
      <div className="aa-header">
        <div className="aa-badge">✦ Analytics</div>
        <h2 className="aa-title">Ads Analytics</h2>
        <p className="aa-sub">Monitor performance of all advertisements</p>
      </div>

      {/* STATS */}
      <div className="aa-stats-row">
        <div className="aa-stat">
          <span className="aa-stat-icon">📊</span>
          <span className="aa-stat-num">{ads.length}</span>
          <span className="aa-stat-label">Total Ads</span>
        </div>
        
        <div className="aa-stat">
          <span className="aa-stat-icon">👁</span>
          <span className="aa-stat-num">{totalViews}</span>
          <span className="aa-stat-label">Total Views</span>
        </div>
        <div className="aa-stat">
          <span className="aa-stat-icon">🟢</span>
          <span className="aa-stat-num">{runningCount}</span>
          <span className="aa-stat-label">Running</span>
        </div>
        <div className="aa-stat">
          <span className="aa-stat-icon">🔴</span>
          <span className="aa-stat-num">{stoppedCount}</span>
          <span className="aa-stat-label">Stopped</span>
        </div>
      </div>

      {/* CONTROLS */}
      <div className="aa-controls">
        <input
          className="aa-search"
          placeholder="🔍 Search ads..."
          value={search}
          onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
        />
        <div className="aa-filter-btns">
          {["all","running","stopped"].map(f => (
            <button
              key={f}
              className={`aa-filter-btn ${filter === f ? "aa-filter-active" : ""}`}
              onClick={() => { setFilter(f); setCurrentPage(1); }}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* TABLE */}
      <div className="aa-table-wrap">
        <table className="aa-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Type</th>
              <th>Preview</th>
              <th>Views / Max</th>
              <th>Progress</th>
              <th>Status</th>
              <th>Created</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentAds.length === 0 ? (
              <tr><td colSpan={8} className="aa-empty">No ads found</td></tr>
            ) : currentAds.map((ad, i) => {
              const stopped  = ad.views >= ad.maxViews;
              const progress = getProgress(ad);

              return (
                <tr key={ad._id}>
                  <td className="aa-td-num">{(currentPage - 1) * adsPerPage + i + 1}</td>
                  <td>
                    <span className="aa-type-badge">
                      {TYPE_ICONS[ad.adType] || "📄"} {ad.adType}
                    </span>
                  </td>
                  <td>
                    {ad.adType === "image" && (
                      <img src={ad.image} className="aa-preview-img" alt="" />
                    )}
                    {ad.adType === "video" && (
                      <video src={ad.image} className="aa-preview-video" />
                    )}
                    {ad.adType === "text" && (
                      <span className="aa-preview-text">{(ad.text || "").slice(0, 40)}…</span>
                    )}
                  </td>
                  <td>
                    <span className="aa-views-text">
                      {ad.views || 0} <span className="aa-td-muted">/ {ad.maxViews || 0}</span>
                    </span>
                  </td>
                  <td>
                    <div className="aa-progress-bar">
                      <div
                        className="aa-progress-fill"
                        style={{
                          width: `${progress}%`,
                          background: stopped
                            ? "rgba(239,68,68,0.7)"
                            : "linear-gradient(90deg, #7c3aed, #06b6d4)",
                        }}
                      />
                    </div>
                    <span className="aa-progress-pct">{progress}%</span>
                  </td>
                  <td>
                    <span className={`aa-status-badge ${stopped ? "aa-status-stopped" : "aa-status-running"}`}>
                      {stopped ? "⛔ Stopped" : "✅ Running"}
                    </span>
                  </td>
                  <td className="aa-td-muted">
                    {new Date(ad.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </td>
                  <td>
                    <button className="aa-open-btn" onClick={() => setSelectedAd(ad)}>
                      Open ↗
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="aa-pagination">
          <button className="aa-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>← Prev</button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              className={`aa-page-btn ${currentPage === i + 1 ? "aa-page-active" : ""}`}
              onClick={() => setCurrentPage(i + 1)}
            >{i + 1}</button>
          ))}
          <button className="aa-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {/* POPUP */}
      {selectedAd && (
        <div className="aa-popup-overlay" onClick={() => setSelectedAd(null)}>
          <div className="aa-popup" onClick={e => e.stopPropagation()}>

            <button className="aa-popup-close" onClick={() => setSelectedAd(null)}>✕</button>

            {/* POPUP HEADER */}
            <div className="aa-popup-header">
              <span className="aa-popup-type">
                {TYPE_ICONS[selectedAd.adType]} {selectedAd.adType} Ad
              </span>
              <span className={`aa-status-badge ${selectedAd.views >= selectedAd.maxViews ? "aa-status-stopped" : "aa-status-running"}`}>
                {selectedAd.views >= selectedAd.maxViews ? "⛔ Stopped" : "✅ Running"}
              </span>
            </div>

            {/* STATS GRID */}
            <div className="aa-popup-stats">
              <div className="aa-popup-stat">
                <span className="aa-popup-stat-label">Total Views</span>
                <span className="aa-popup-stat-num">{selectedAd.views || 0}</span>
              </div>
              <div className="aa-popup-stat">
                <span className="aa-popup-stat-label">Max Views</span>
                <span className="aa-popup-stat-num">{selectedAd.maxViews || 0}</span>
              </div>
              <div className="aa-popup-stat">
                <span className="aa-popup-stat-label">Progress</span>
                <span className="aa-popup-stat-num">{getProgress(selectedAd)}%</span>
              </div>
            </div>

            {/* PROGRESS BAR */}
            <div className="aa-popup-progress-wrap">
              <div className="aa-popup-progress-bar">
                <div
                  className="aa-popup-progress-fill"
                  style={{
                    width: `${getProgress(selectedAd)}%`,
                    background: selectedAd.views >= selectedAd.maxViews
                      ? "rgba(239,68,68,0.7)"
                      : "linear-gradient(90deg, #7c3aed, #06b6d4)",
                  }}
                />
              </div>
            </div>

            <p className="aa-popup-date">
              🕒 Created: {new Date(selectedAd.createdAt).toLocaleString()}
            </p>

            {/* MEDIA */}
            {selectedAd.adType === "image" && (
              <img src={selectedAd.image} className="aa-popup-media-img" alt="" />
            )}
            {selectedAd.adType === "video" && (
              <video src={selectedAd.image} controls autoPlay className="aa-popup-media-video" />
            )}
            {selectedAd.adType === "text" && (
              <div className="aa-popup-text-content">{selectedAd.text}</div>
            )}

            {/* LINK */}
            {selectedAd.link && (
              <a className="aa-popup-link" href={selectedAd.link} target="_blank" rel="noreferrer">
                🔗 Visit Ad Link ↗
              </a>
            )}

          </div>
        </div>
      )}
    </div>
  );
}

export default AdsAnalytics;